<script>
import urlHelper from "@/utils/URLHelper";

export default {
  name: "HowToApplyView",
  data() {
    return {
      admissionsEmail: process.env.VUE_APP_FCP_ADMISSIONS_EMAIL || "n/a",
      phoneNumber: process.env.VUE_APP_FCP_PHONE_NO || "n/a",
      applyUrl: urlHelper.getApplyUrl(),
      portalLoginUrl: urlHelper.getPortalUrl("/auth"),
    };
  },
  mounted() {
    document.title = "How to Apply - First City Polytechnic";
  },
};
</script>

<template>
  <div class="how-to-apply">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-overlay"></div>
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-7 col-md-8">
            <div class="hero-content text-start">
              <h1 class="hero-title">How to Apply</h1>
              <p class="hero-subtitle">
                Your step-by-step guide to becoming a student at First City
                Polytechnic. Follow each stage below to complete your
                application.
              </p>
              <a :href="applyUrl" class="btn-hero">
                Start Application
                <i class="bi bi-arrow-right ms-2"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Quick Actions -->
    <section class="actions-section">
      <div class="container">
        <div class="row g-4">
          <div class="col-md-4">
            <a :href="applyUrl" class="action-card action-card-primary">
              <div class="action-icon">
                <i class="bi bi-pencil-square"></i>
              </div>
              <h3 class="action-title">Start Application</h3>
              <p class="action-text">
                New student? Begin your application here.
              </p>
              <span class="action-link">
                Apply Now <i class="bi bi-arrow-right ms-1"></i>
              </span>
            </a>
          </div>
          <div class="col-md-4">
            <a :href="portalLoginUrl" class="action-card action-card-dark">
              <div class="action-icon">
                <i class="bi bi-box-arrow-in-right"></i>
              </div>
              <h3 class="action-title">Sign In to Portal</h3>
              <p class="action-text">
                Already registered? Continue your application.
              </p>
              <span class="action-link">
                Sign In <i class="bi bi-arrow-right ms-1"></i>
              </span>
            </a>
          </div>
          <div class="col-md-4">
            <div class="action-card action-card-accent">
              <div class="action-icon">
                <i class="bi bi-question-circle"></i>
              </div>
              <h3 class="action-title">Need Help?</h3>
              <p class="action-text">
                Contact the Admissions Office for assistance.
              </p>
              <a :href="`mailto:${admissionsEmail}`" class="action-link">
                {{ admissionsEmail }}
                <i class="bi bi-arrow-right ms-1"></i>
              </a>
              <br />
              <a :href="`tel:${phoneNumber}`" class="action-link mt-1">
                {{ phoneNumber }}
                <i class="bi bi-telephone ms-1"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stepper Section -->
    <section class="stepper-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-8 mx-auto">
            <div class="section-header text-center mb-5">
              <h2 class="section-title">Application Process</h2>
              <p class="section-subtitle">
                Complete these steps to secure your admission
              </p>
            </div>

            <div class="stepper">
              <!-- Step 1 -->
              <div class="step-item">
                <div class="step-marker">
                  <div class="step-number">1</div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase">Registration</span>
                  </div>
                  <h3 class="step-title">Create an Account</h3>
                  <p class="step-description">
                    Register on our application portal with your personal
                    details and select your desired programme. You'll receive an
                    <strong>Application Number</strong> and
                    <strong>temporary password</strong> to access your portal.
                  </p>
                  <a :href="applyUrl" class="step-cta">
                    <i class="bi bi-person-plus me-2"></i>
                    Apply Now
                  </a>
                </div>
              </div>

              <!-- Step 2 -->
              <div class="step-item">
                <div class="step-marker">
                  <div class="step-number">2</div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase">Payment</span>
                  </div>
                  <h3 class="step-title">Pay Portal Fee</h3>
                  <p class="step-description">
                    Log in to the student portal and pay the portal fee. This
                    activates full access to your application form.
                  </p>
                  <a :href="portalLoginUrl" class="step-cta step-cta-outline">
                    <i class="bi bi-box-arrow-in-right me-2"></i>
                    Sign In to Portal
                  </a>
                </div>
              </div>

              <!-- Step 3 -->
              <div class="step-item">
                <div class="step-marker">
                  <div class="step-number">3</div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase">Application</span>
                  </div>
                  <h3 class="step-title">Complete Application Form</h3>
                  <p class="step-description">
                    Fill in all required details and upload the following
                    documents:
                  </p>
                  <ul class="doc-list">
                    <li>
                      <i class="bi bi-file-earmark-check"></i> O'Level Result
                      (WAEC/NECO)
                    </li>
                    <li>
                      <i class="bi bi-file-earmark-check"></i> JAMB Result
                    </li>
                    <li>
                      <i class="bi bi-file-earmark-check"></i> Birth Certificate
                    </li>
                    <li>
                      <i class="bi bi-file-earmark-check"></i> Passport
                      Photograph
                    </li>
                    <li>
                      <i class="bi bi-file-earmark-check"></i> Any other
                      required documents
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Step 4 -->
              <div class="step-item step-item-wait">
                <div class="step-marker">
                  <div class="step-number">4</div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase step-phase-wait">Review</span>
                  </div>
                  <h3 class="step-title">Admission Review</h3>
                  <p class="step-description">
                    Your application is reviewed by the Admissions team. You
                    will be notified of the outcome via your registered email
                    and portal dashboard.
                  </p>
                  <div class="step-info">
                    <i class="bi bi-clock-history me-2"></i>
                    Please allow time for the review process
                  </div>
                </div>
              </div>

              <!-- Step 5 -->
              <div class="step-item">
                <div class="step-marker">
                  <div class="step-number">5</div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase">Payment</span>
                  </div>
                  <h3 class="step-title">Pay Acceptance Fee</h3>
                  <p class="step-description">
                    Once your application is approved, log in and pay the
                    acceptance fee to confirm your admission and secure your
                    spot.
                  </p>
                </div>
              </div>

              <!-- Step 6 -->
              <div class="step-item">
                <div class="step-marker">
                  <div class="step-number">6</div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase step-phase-wait">Clearance</span>
                  </div>
                  <h3 class="step-title">School Clearance</h3>
                  <p class="step-description">
                    Visit the campus for physical document verification and
                    clearance by the relevant departments. Bring your original
                    documents.
                  </p>
                </div>
              </div>

              <!-- Step 7 -->
              <div class="step-item">
                <div class="step-marker">
                  <div class="step-number">7</div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase">Payment</span>
                  </div>
                  <h3 class="step-title">Pay Administrative Fee</h3>
                  <p class="step-description">
                    Pay the required administrative fee through the student
                    portal to continue the enrolment process.
                  </p>
                </div>
              </div>

              <!-- Step 8 -->
              <div class="step-item">
                <div class="step-marker">
                  <div class="step-number">8</div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase">Payment</span>
                  </div>
                  <h3 class="step-title">Pay School Fees</h3>
                  <p class="step-description">
                    Complete your tuition and school fees payment. All payments
                    are made securely through the student portal.
                  </p>
                </div>
              </div>

              <!-- Step 9 (Final) -->
              <div class="step-item step-item-final">
                <div class="step-marker">
                  <div class="step-number step-number-final">
                    <i class="bi bi-check-lg"></i>
                  </div>
                </div>
                <div class="step-content">
                  <div class="step-header">
                    <span class="step-phase step-phase-success">Complete</span>
                  </div>
                  <h3 class="step-title">
                    Receive Matric Number & Credentials
                  </h3>
                  <p class="step-description">
                    After successful payment, you will receive your:
                  </p>
                  <div class="credentials-card">
                    <div class="credential-item">
                      <i class="bi bi-person-badge"></i>
                      <div>
                        <strong>Matric Number</strong>
                        <span>Your unique student identifier</span>
                      </div>
                    </div>
                    <div class="credential-item">
                      <i class="bi bi-key"></i>
                      <div>
                        <strong>New Portal Password</strong>
                        <span>Sent to your registered email</span>
                      </div>
                    </div>
                  </div>
                  <p class="step-description mt-3">
                    Use these credentials to access the full student portal for
                    course registration and more.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* ── Hero ── */
.hero-section {
  background-image: url("@/assets/img/banner.jpeg");
  background-size: cover;
  background-position: center;
  min-height: 400px;
  position: relative;
  overflow: hidden;
  padding: 120px 0;
  display: flex;
  align-items: center;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(25, 40, 54, 1) 0%,
    rgba(25, 40, 54, 0.85) 40%,
    rgba(25, 40, 54, 0.3) 70%,
    rgba(25, 40, 54, 0) 100%
  );
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  color: white;
}

.hero-title {
  font-weight: 700;
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.hero-subtitle {
  font-size: 1.2rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
  max-width: 480px;
  margin-bottom: 2rem;
}

.btn-hero {
  display: inline-flex;
  align-items: center;
  padding: 14px 36px;
  background: #edb71c;
  color: #192836;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(237, 183, 28, 0.4);
}

.btn-hero:hover {
  background: #d4a419;
  color: #192836;
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(237, 183, 28, 0.5);
}

/* ── Stepper Section ── */
.stepper-section {
  padding: 80px 0;
  background: #f8f9fa;
}

.section-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #192836;
  position: relative;
  padding-bottom: 15px;
}

.section-title::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 70px;
  height: 4px;
  background: #edb71c;
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
  margin-top: 1rem;
}

/* ── Stepper Items ── */
.stepper {
  position: relative;
}

.step-item {
  display: flex;
  gap: 1.5rem;
  position: relative;
}

.step-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.step-number {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #374c91, #192836);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  z-index: 2;
  box-shadow: 0 4px 12px rgba(55, 76, 145, 0.3);
  flex-shrink: 0;
}

.step-number-final {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #198754, #146c43);
  font-size: 1.5rem;
  box-shadow: 0 4px 16px rgba(25, 135, 84, 0.35);
}

.step-line {
  width: 3px;
  flex: 1;
  background: linear-gradient(to bottom, #374c91, #dee2e6);
  border-radius: 3px;
  min-height: 20px;
}

.step-item-final .step-marker .step-line {
  display: none;
}

.step-content {
  background: white;
  border-radius: 16px;
  padding: 1.75rem 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #e9ecef;
  flex: 1;
  transition: all 0.3s ease;
}

.step-content:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  border-color: #edb71c;
}

.step-header {
  margin-bottom: 0.5rem;
}

.step-phase {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #374c91;
  background: rgba(55, 76, 145, 0.08);
  padding: 3px 12px;
  border-radius: 50px;
}

.step-phase-wait {
  color: #e67e22;
  background: rgba(230, 126, 34, 0.08);
}

.step-phase-success {
  color: #198754;
  background: rgba(25, 135, 84, 0.08);
}

.step-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #192836;
  margin-bottom: 0.75rem;
  margin-top: 0.5rem;
}

.step-description {
  font-size: 0.95rem;
  color: #6c757d;
  line-height: 1.7;
  margin-bottom: 0;
}

/* Step CTA buttons */
.step-cta {
  display: inline-flex;
  align-items: center;
  padding: 10px 28px;
  background: #374c91;
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.step-cta:hover {
  background: #192836;
  color: white;
  transform: translateX(4px);
}

.step-cta-outline {
  background: transparent;
  color: #374c91;
  border: 2px solid #374c91;
}

.step-cta-outline:hover {
  background: #374c91;
  color: white;
}

/* Doc list */
.doc-list {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
}

.doc-list li {
  padding: 6px 0;
  font-size: 0.92rem;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.doc-list li i {
  color: #198754;
  font-size: 1rem;
}

/* Step info */
.step-info {
  margin-top: 0.75rem;
  padding: 10px 16px;
  background: #fff8e1;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #856404;
  display: inline-flex;
  align-items: center;
}

/* Credentials card */
.credentials-card {
  background: #f0f7f0;
  border-radius: 12px;
  padding: 1.25rem;
  margin-top: 0.75rem;
  border: 1px solid #c3e6c3;
}

.credential-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 8px 0;
}

.credential-item + .credential-item {
  border-top: 1px solid #c3e6c3;
  margin-top: 8px;
  padding-top: 16px;
}

.credential-item i {
  font-size: 1.5rem;
  color: #198754;
  flex-shrink: 0;
}

.credential-item strong {
  display: block;
  font-size: 0.95rem;
  color: #192836;
}

.credential-item span {
  font-size: 0.82rem;
  color: #6c757d;
}

/* ── Actions Section ── */
.actions-section {
  padding: 80px 0;
  background: white;
}

.action-card {
  display: block;
  background: #f8f9fa;
  border-radius: 16px;
  padding: 2.5rem 2rem;
  text-align: center;
  text-decoration: none;
  height: 100%;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.action-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.action-card-primary {
  border-color: #374c91;
}

.action-card-primary:hover {
  background: #374c91;
}

.action-card-primary:hover .action-title,
.action-card-primary:hover .action-text,
.action-card-primary:hover .action-link {
  color: white;
}

.action-card-primary:hover .action-icon {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.action-card-dark {
  border-color: #192836;
}

.action-card-dark:hover {
  background: #192836;
}

.action-card-dark:hover .action-title,
.action-card-dark:hover .action-text,
.action-card-dark:hover .action-link {
  color: white;
}

.action-card-dark:hover .action-icon {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.action-card-accent {
  border-color: #edb71c;
}

.action-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #374c91, #192836);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  margin: 0 auto 1.25rem;
  box-shadow: 0 4px 16px rgba(55, 76, 145, 0.2);
  transition: all 0.3s ease;
}

.action-card-accent .action-icon {
  background: linear-gradient(135deg, #edb71c, #d4a419);
  color: #192836;
  box-shadow: 0 4px 16px rgba(237, 183, 28, 0.3);
}

.action-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #192836;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
}

.action-text {
  font-size: 0.95rem;
  color: #6c757d;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
}

.action-link {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374c91;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
}

.action-card-accent .action-link {
  color: #192836;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .hero-section {
    padding: 80px 0;
    min-height: auto;
  }

  .hero-overlay {
    background: linear-gradient(
      to right,
      rgba(25, 40, 54, 0.95) 0%,
      rgba(25, 40, 54, 0.85) 60%,
      rgba(25, 40, 54, 0.7) 100%
    );
  }

  .hero-content {
    text-align: center !important;
  }

  .hero-subtitle {
    margin-left: auto;
    margin-right: auto;
  }

  .hero-title {
    font-size: 2.25rem;
  }

  .section-title {
    font-size: 1.85rem;
  }

  .step-content {
    padding: 1.25rem 1.5rem;
  }

  .step-title {
    font-size: 1.1rem;
  }

  .action-card {
    padding: 2rem 1.5rem;
  }
}

@media (max-width: 576px) {
  .hero-section {
    padding: 60px 0;
  }

  .hero-overlay {
    background: linear-gradient(
      to right,
      rgba(25, 40, 54, 0.98) 0%,
      rgba(25, 40, 54, 0.92) 100%
    );
  }

  .hero-title {
    font-size: 1.85rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .step-item {
    gap: 1rem;
  }

  .step-number {
    width: 36px;
    height: 36px;
    font-size: 0.95rem;
  }

  .step-number-final {
    width: 42px;
    height: 42px;
    font-size: 1.25rem;
  }

  .step-content {
    padding: 1rem 1.25rem;
    margin-bottom: 1rem;
  }

  .step-title {
    font-size: 1.05rem;
  }

  .step-description {
    font-size: 0.9rem;
  }

  .credentials-card {
    padding: 1rem;
  }

  .credential-item {
    gap: 0.75rem;
  }

  .credential-item i {
    font-size: 1.25rem;
  }
}
</style>
